import { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible';
import { ListFilter, RefreshCw, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getSyncLog } from '@/services/integrationsRedesignService';
import type { SyncLogEntry, SyncLogPage } from '@/types/integrations-redesign';

export interface SyncLogPageInfo {
  totalCount: number;
  totalPages: number;
  loading: boolean;
}

export interface SyncLogSectionHandle {
  refresh: () => void;
}

interface SyncLogSectionProps {
  companyId: number;
  page: number;
  onPageChange: (page: number) => void;
  onPageInfoChange?: (info: SyncLogPageInfo) => void;
}

function StatusBadge({ status }: { status: SyncLogEntry['status'] }) {
  switch (status) {
    case 'completed':
      return <Badge variant="outline" className="bg-success-50 text-success-700 border-success-200 text-[11px]">Completed</Badge>;
    case 'failed':
      return <Badge variant="outline" className="bg-error-50 text-error-700 border-error-200 text-[11px]">Failed</Badge>;
    case 'partial':
      return <Badge variant="outline" className="bg-warning-50 text-warning-700 border-warning-200 text-[11px]">Partial</Badge>;
    case 'started':
      return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[11px]">In Progress</Badge>;
    case 'interrupted':
      return <Badge variant="outline" className="bg-warning-50 text-warning-700 border-warning-200 text-[11px]">Interrupted</Badge>;
    default:
      return <Badge variant="outline" className="text-[11px]">{status}</Badge>;
  }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDate(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, '0');
  return `${day} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function RecordCounts({ entry }: { entry: SyncLogEntry }) {
  const parts: string[] = [];
  if (entry.recordsCreated > 0) parts.push(`${entry.recordsCreated} created`);
  if (entry.recordsUpdated > 0) parts.push(`${entry.recordsUpdated} updated`);
  if (entry.recordsFailed > 0) parts.push(`${entry.recordsFailed} failed`);
  if (parts.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="flex flex-col leading-tight" title={parts.join(', ')}>
      {parts.map(part => (
        <span key={part} className="whitespace-normal break-words">
          {part}
        </span>
      ))}
    </span>
  );
}

const INTEGRATION_TYPES = [
  { value: 'finance', label: 'Finance' },
  { value: 'timesheet', label: 'Timesheet' },
  { value: 'expense', label: 'Expense' },
  { value: 'planner', label: 'Planner' },
  { value: 'purchasing', label: 'Purchasing' },
];

const STATUSES = [
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'partial', label: 'Partial' },
  { value: 'started', label: 'In Progress' },
  { value: 'interrupted', label: 'Interrupted' },
];

const TRIGGER_TYPES = [
  { value: 'manual', label: 'Manual' },
  { value: 'manual-full-resync', label: 'Manual full resync' },
  { value: 'auto-scheduled', label: 'Auto scheduled' },
  { value: 'webhook', label: 'Webhook' },
  { value: 'system', label: 'System' },
];

export const PAGE_SIZE = 20;

function FilterIcon({ active }: { active: boolean }) {
  return (
    <ListFilter
      className={cn('h-3 w-3 shrink-0', active ? 'text-primary' : 'text-muted-foreground/60')}
    />
  );
}

interface OptionFilterProps {
  label: string;
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
}

function OptionFilter({ label, options, value, onChange }: OptionFilterProps) {
  const [open, setOpen] = useState(false);

  function select(next: string) {
    onChange(next);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Filter by ${label}`}
          className="inline-flex items-center rounded p-0.5 hover:bg-muted"
        >
          <FilterIcon active={value !== 'all'} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-44 p-1">
        <button
          type="button"
          className={cn(
            'w-full rounded px-2 py-1.5 text-left text-xs hover:bg-muted',
            value === 'all' && 'font-medium text-primary',
          )}
          onClick={() => select('all')}
        >
          All
        </button>
        {options.map(o => (
          <button
            key={o.value}
            type="button"
            className={cn(
              'w-full rounded px-2 py-1.5 text-left text-xs hover:bg-muted',
              value === o.value && 'font-medium text-primary',
            )}
            onClick={() => select(o.value)}
          >
            {o.label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function TextFilter({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value);

  useEffect(() => {
    setText(value);
  }, [value]);

  function apply() {
    onChange(text.trim());
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Filter by ${label}`}
          className="inline-flex items-center rounded p-0.5 hover:bg-muted"
        >
          <FilterIcon active={value !== ''} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-52 p-2 space-y-2">
        <Input
          autoFocus
          value={text}
          placeholder="Contains…"
          className="h-7 text-xs"
          onChange={e => setText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') apply();
          }}
        />
        <div className="flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => {
              setText('');
              onChange('');
              setOpen(false);
            }}
          >
            Clear
          </Button>
          <Button size="sm" className="h-6 px-2 text-xs" onClick={apply}>
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const SyncLogSection = forwardRef<SyncLogSectionHandle, SyncLogSectionProps>(
  function SyncLogSection({ companyId, page, onPageChange, onPageInfoChange }, ref) {
    const [data, setData] = useState<SyncLogPage | null>(null);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterTrigger, setFilterTrigger] = useState('all');
    const [filterBy, setFilterBy] = useState('');
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

    const fetchData = useCallback(async () => {
      setLoading(true);
      try {
        const result = await getSyncLog(
          companyId,
          filterType === 'all' ? undefined : filterType,
          page,
          PAGE_SIZE,
          filterStatus === 'all' ? undefined : filterStatus,
          filterTrigger === 'all' ? undefined : filterTrigger,
          filterBy === '' ? undefined : filterBy,
        );
        setData(result);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    }, [companyId, filterType, filterStatus, filterTrigger, filterBy, page]);

    useEffect(() => {
      fetchData();
    }, [fetchData]);

    useImperativeHandle(ref, () => ({
      refresh: fetchData,
    }));

    const totalCount = data?.totalCount ?? 0;
    const totalPages = data ? Math.ceil(data.totalCount / PAGE_SIZE) : 0;

    useEffect(() => {
      onPageInfoChange?.({ totalCount, totalPages, loading });
    }, [totalCount, totalPages, loading, onPageInfoChange]);

    function toggleExpanded(id: number) {
      setExpandedRows(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }

    function changeFilter(setter: (value: string) => void) {
      return (value: string) => {
        setter(value);
        onPageChange(1);
      };
    }

    return (
      <div className="space-y-2">
        {/* Header row with inline refresh */}
        <div className="relative border-b">
          <Button
            variant="ghost"
            size="sm"
            aria-label="Refresh sync log"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
            onClick={fetchData}
            disabled={loading}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <div className="grid grid-cols-[minmax(0,1fr)_70px_80px_80px_minmax(0,1.1fr)_80px] gap-2 pl-3 pr-10 py-2 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
            <span>Date / Time</span>
            <span className="flex items-center gap-1">
              Type
              <OptionFilter
                label="Type"
                options={INTEGRATION_TYPES}
                value={filterType}
                onChange={changeFilter(setFilterType)}
              />
            </span>
            <span className="flex items-center gap-1">
              Status
              <OptionFilter
                label="Status"
                options={STATUSES}
                value={filterStatus}
                onChange={changeFilter(setFilterStatus)}
              />
            </span>
            <span className="flex items-center gap-1">
              Trigger
              <OptionFilter
                label="Trigger"
                options={TRIGGER_TYPES}
                value={filterTrigger}
                onChange={changeFilter(setFilterTrigger)}
              />
            </span>
            <span className="flex items-center gap-1">
              Records
              <span className="inline-flex items-center p-0.5" aria-hidden="true">
                <ListFilter className="h-3 w-3 shrink-0 invisible" />
              </span>
            </span>
            <span className="flex items-center gap-1">
              By
              <TextFilter label="By" value={filterBy} onChange={changeFilter(setFilterBy)} />
            </span>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded-md" />
            ))}
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="text-center py-10 text-sm text-muted-foreground">
            No sync log entries found.
          </div>
        ) : (
          <div className="divide-y">
            {data.items.map(entry => (
              <Collapsible
                key={entry.id}
                open={expandedRows.has(entry.id)}
                onOpenChange={() => entry.status === 'failed' && entry.errorMessage && toggleExpanded(entry.id)}
              >
                <CollapsibleTrigger asChild>
                  <div
                    className={`grid grid-cols-[minmax(0,1fr)_70px_80px_80px_minmax(0,1.1fr)_80px] gap-2 pl-3 pr-10 py-2 text-xs items-center hover:bg-muted/40 transition-colors ${
                      entry.status === 'failed' && entry.errorMessage ? 'cursor-pointer' : ''
                    }`}
                  >
                    <span className="flex flex-col leading-tight min-w-0" title={entry.startedAt}>
                      <span>{formatDate(entry.startedAt)}</span>
                      <span className="text-[11px] text-muted-foreground">{formatTime(entry.startedAt)}</span>
                    </span>
                    <span className="capitalize truncate min-w-0">{entry.integrationType}</span>
                    <span><StatusBadge status={entry.status} /></span>
                    <span
                      className="min-w-0 whitespace-normal break-words leading-tight text-muted-foreground"
                      title={entry.triggerType}
                    >
                      {entry.triggerType}
                    </span>
                    <span className="min-w-0"><RecordCounts entry={entry} /></span>
                    <span className="truncate min-w-0 text-muted-foreground" title={entry.initiatedBy ?? undefined}>
                      {entry.initiatedBy ?? '—'}
                    </span>
                  </div>
                </CollapsibleTrigger>
                {entry.status === 'failed' && entry.errorMessage && (
                  <CollapsibleContent>
                    <div className="px-3 pb-3 pt-1">
                      <div className="flex items-start gap-2 rounded-md bg-error-50 border border-error-200 p-2.5 text-xs text-error-700">
                        <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span className="break-words">{entry.errorMessage}</span>
                      </div>
                    </div>
                  </CollapsibleContent>
                )}
              </Collapsible>
            ))}
          </div>
        )}
      </div>
    );
  },
);

export default SyncLogSection;
